import React, { useEffect, useState } from 'react';
import { listMockSessions, listMockAnswers } from '../../api/mockSessions';

/**
 * MockPerformanceSection
 *
 * Displays the signed-in candidate's mock-interview history. Designed to be
 * dropped into the Phase 7 candidate profile drawer or any candidate-facing
 * page. The backend `/api/mock-sessions` endpoint scopes to the JWT subject,
 * so this component shows the *current user's* sessions only.
 */
export default function MockPerformanceSection() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [answers, setAnswers]   = useState({});

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res  = await listMockSessions();
        const data = Array.isArray(res.data) ? res.data : [];
        if (!cancel) setSessions(data);
      } catch (err) {
        console.error('[MockPerformanceSection] load failed:', err.message);
        if (!cancel) setError(err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const toggle = async (sessionId) => {
    if (expanded === sessionId) { setExpanded(null); return; }
    setExpanded(sessionId);
    if (!answers[sessionId]) {
      try {
        const res  = await listMockAnswers(sessionId);
        const list = Array.isArray(res.data) ? res.data : [];
        setAnswers((prev) => ({ ...prev, [sessionId]: list }));
      } catch (err) {
        console.error('[MockPerformanceSection] answers load failed:', err.message);
      }
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-400">Loading mock-interview history…</div>;
  }
  if (error) {
    return <div className="text-sm text-red-400">Could not load: {error}</div>;
  }
  if (!sessions.length) {
    return <div className="text-sm text-slate-400">No mock interviews yet.</div>;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-200">Mock interview performance</h3>
      <ul className="space-y-2">
        {sessions.map((s) => {
          const score = s.overallScore != null ? Math.round(s.overallScore) : null;
          const when  = s.completedAt
            ? new Date(s.completedAt).toLocaleString()
            : (s.createdAt ? new Date(s.createdAt).toLocaleString() : '');
          const isOpen = expanded === s.id;
          return (
            <li key={s.id} className="border border-slate-800 rounded-lg bg-slate-900/40">
              <button
                type="button"
                onClick={() => toggle(s.id)}
                className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-800/40"
              >
                <span className="flex flex-col">
                  <span className="text-slate-200">
                    {s.mode} · {s.totalQuestions} question{s.totalQuestions === 1 ? '' : 's'}
                  </span>
                  <span className="text-xs text-slate-500">{when}</span>
                </span>
                <span className="flex items-center gap-3">
                  <span className={`text-sm font-semibold ${score == null ? 'text-slate-500'
                      : score >= 70 ? 'text-emerald-400'
                      : score >= 40 ? 'text-amber-400'
                      : 'text-red-400'}`}>
                    {score == null ? '-' : `${score}/100`}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-slate-500">
                    {s.status}
                  </span>
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-slate-800 px-3 py-2 space-y-2">
                  {(answers[s.id] || []).length === 0 && (
                    <div className="text-xs text-slate-500">No answers recorded.</div>
                  )}
                  {(answers[s.id] || []).map((a) => (
                    <div key={a.id} className="text-xs text-slate-300">
                      <div className="flex justify-between">
                        <span className="font-mono">{a.language || 'unknown'}</span>
                        <span>{a.aiScore != null ? `${Math.round(a.aiScore)}/100` : '-'}</span>
                      </div>
                      {a.aiFeedback?.feedback && (
                        <div className="mt-1 text-slate-400">{a.aiFeedback.feedback}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
